import { useRef, useEffect, useState } from "react";
import { useOpenGraphData } from "../hooks";
import useUtmify from "../utm";
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
} from "@floating-ui/react";
import { Link } from "lucide-react";

interface ExternalLinkProps {
  href: string | undefined;
  children: React.ReactNode;
  [key: string]: any;
}

export function ExternalLink({ href, children, ...props }: ExternalLinkProps) {
  const utmify = useUtmify();
  try {
    if (href && href.startsWith("http")) {
      href = utmify(href, "message_link");
    }
  } catch (error) {
    // Silently fall back to original href
    console.warn("Failed to utmify link:", error);
  }

  // [[<number>]] in markdown; but outer [] already consumed by markdown parser.
  // So we check if the children is a string and has form [].
  const isCiteTag =
    typeof children === "string" &&
    /^\[[^\]]+\]/.test(children) &&
    href?.startsWith("http");
  return isCiteTag ? (
    <CiteLink href={href} children={children} {...props} />
  ) : (
    <DefaultLink href={href} children={children} {...props} />
  );
}

function getDomain(url: string): string {
  try {
    const parsedUrl = new URL(url);
    return parsedUrl.hostname.replace(/^www\./, "");
  } catch (error) {
    console.error("Invalid URL:", url, error);
    return url;
  }
}

// TODO(liamvdv): add a security check to ensure that the link is not malicious
//                / collect link data via API.
export function CiteLink(props: ExternalLinkProps) {
  const { href } = props;
  const [showTooltip, setShowTooltip] = useState(false);
  const [isVisible, setIsVisible] = useState(false); // For transition control

  const citationDomRef = useRef<HTMLElement | null>(null);
  const tooltipDomRef = useRef<HTMLDivElement | null>(null);
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { metaData, status, error: ogError } = useOpenGraphData(href || "");

  const title = metaData?.hg_title || "No title";
  const domain = getDomain(href || "");

  // Floating UI hook setup
  const { x, y, strategy, refs, context } = useFloating({
    open: showTooltip,
    onOpenChange: setShowTooltip,
    placement: "top",
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(8),
      flip({
        padding: 5,
      }),
      shift({
        padding: 5,
      }),
    ],
  });

  // Clear any existing timeout
  const clearHoverTimeout = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
  };

  // Start fade out timer
  const startFadeOutTimer = () => {
    clearHoverTimeout();
    hoverTimeoutRef.current = setTimeout(() => {
      setIsVisible(false);
      // Wait for transition to complete before hiding tooltip
      setTimeout(() => {
        if (!isHovering()) {
          setShowTooltip(false);
        }
      }, 200); // Match transition duration
    }, 500); // Wait 500ms before starting fade out
  };

  // Check if either element is being hovered
  const isHovering = () => {
    const citationHovered = citationDomRef.current?.matches(":hover") || false;
    const tooltipHovered = tooltipDomRef.current?.matches(":hover") || false;
    return citationHovered || tooltipHovered;
  };

  const handleMouseEnter = () => {
    clearHoverTimeout();
    setShowTooltip(true);
    // Small delay to allow DOM to update before making visible
    setTimeout(() => setIsVisible(true), 10);
  };

  const handleMouseLeave = () => {
    startFadeOutTimer();
  };

  const handleTooltipMouseEnter = () => {
    clearHoverTimeout();
    setIsVisible(true); // Ensure it's visible if mouse re-enters tooltip
  };

  const handleTooltipMouseLeave = () => {
    startFadeOutTimer();
  };

  // MODIFIED handleClick function
  const handleClick = (e: React.MouseEvent) => {
    // Always clear any pending hover-initiated close actions when a click occurs.
    clearHoverTimeout();

    if (!showTooltip) {
      // If the tooltip is currently closed, open it.
      e.preventDefault(); // This click's primary action is to open the tooltip.
      setShowTooltip(true);
      setTimeout(() => setIsVisible(true), 10);
    } else {
      setIsVisible(true);
    }
  };

  useEffect(() => {
    return () => {
      clearHoverTimeout();
    };
  }, []);

  const tooltipId = `tooltip-${props.href}`;

  return (
    <>
      <sup>
        <span
          ref={(node) => {
            citationDomRef.current = node;
            refs.setReference(node);
          }}
          onClick={handleClick}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onMouseDown={(e) => e.preventDefault()} // avoid scrolling
          className={`no-underline inline-flex items-center rounded-md text-xs px-1 mx-0.5 transition-all duration-150 cursor-pointer  ${
            showTooltip
              ? "bg-brand text-chat"
              : "bg-gray-200 text-gray-600 hover:bg-brand hover:text-chat"
          }`}
          aria-describedby={showTooltip ? tooltipId : undefined}
          role="button"
          tabIndex={0} // make it focusable
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              handleClick(e as any);
            }
          }}
        >
          {typeof props.children === "string"
            ? props.children.slice(1, -1)
            : props.children}
        </span>
      </sup>

      {/* Tooltip */}
      {showTooltip && (
        <div
          ref={(node) => {
            tooltipDomRef.current = node;
            refs.setFloating(node);
          }}
          id={tooltipId}
          role="tooltip"
          onMouseEnter={handleTooltipMouseEnter}
          onMouseLeave={handleTooltipMouseLeave}
          className={`z-50 bg-white border border-gray-200 rounded-lg shadow-lg max-w-xs transition-opacity duration-200 ease-out ${
            isVisible ? "opacity-100" : "opacity-0"
          }`}
          style={{
            position: strategy,
            left: x ?? 0,
            top: y ?? 0,
          }}
        >
          {status === "loading" && (
            <div className="text-sm text-gray-500 animate-pulse">
              Loading...
            </div>
          )}

          {status === "failed" && (
            <div className="text-sm text-red-500">
              Error: {ogError || "Failed to load preview"}
            </div>
          )}

          {status === "succeeded" && metaData && (
            <a
              href={href}
              target="_blank"
              rel="noreferrer"
              className="block text-xs font-medium transition-colors duration-150 no-underline p-2"
              onClick={(e) => e.stopPropagation()} // Prevents click from bubbling to handleClickOutside
              aria-label={`Visit ${domain}`}
            >
              <div className="flex items-center p-1">
                <Link className="h-3 w-3 mr-1 text-chat-text transition-transform duration-150 group-hover:scale-110" />
                <span className="truncate max-w-[150px] text-chat-text">
                  {domain}
                </span>
              </div>
              <strong className="text-sm font-medium text-chat-text hover:underline line-clamp-2 w-full">
                {title}
              </strong>
            </a>
          )}
          {status === "succeeded" && !metaData && (
            <div className="text-sm text-gray-500">No preview available.</div>
          )}
        </div>
      )}
    </>
  );
}

export function DefaultLink(props: ExternalLinkProps) {
  const { href, children, ...rest } = props;
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="no-underline text-brand hover:text-brand-hover transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-2 focus:ring-offset-chat rounded-sm hover:underline"
    >
      {children}
    </a>
  );
}
